import { useCallback, useEffect, useRef, useState } from "react";
import { HumanSupportBanner } from "@/components/recruiter/HumanSupport";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileUp, LayoutTemplate, Save, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, PageHeader, Spinner } from "@/components/Common";
import { PersonalForm, SummaryForm, ListEditor, SkillsEditor } from "@/components/resume/EditorSections";
import { ScaledDoc } from "@/components/resume/ScaledDoc";
import { TemplatePicker } from "@/components/resume/TemplatePicker";
import { Suggestions } from "@/components/resume/Suggestions";
import { TailorBanner } from "@/components/resume/TailorBanner";
import { OptimizedReview } from "@/components/resume/OptimizedReview";
import { clearOptimized, loadJob, readTailor } from "@/lib/tailor";
import { findAts, loadTools } from "@/lib/tools";
import { TailoredLibrary } from "@/components/resume/TailoredLibrary";
import { addTailored, useTailored } from "@/lib/tailoredLib";
import { setCurrentResume } from "@/lib/atsScores";
import { useResumeUpload } from "@/components/InlineResumeUpload";
import { api, errMsg } from "@/lib/api";
import { normalize, templateById } from "@/lib/resume";
import { downloadPdf, pdfFile } from "@/lib/resumePdf";
import { cn } from "@/lib/utils";

const ComingSoon = () => (
  <Card className="mx-auto max-w-xl text-center" data-testid="resume-studio-coming-soon">
    <Wand2 className="mx-auto mb-3 h-8 w-8 text-[#D4AF37]" />
    <h2 className="font-display text-xl text-zinc-100">Resume Studio is coming soon</h2>
    <p className="mt-2 text-sm text-zinc-400">Edit your boosted resume section by section, pick from 5 ATS-friendly templates and save it straight back as your main resume. This turns on automatically once the RPOPRO backend update goes live.</p>
    <Link to="/profile"><Button variant="outline" className="mt-5" data-testid="coming-soon-profile-link">Manage resumes on Profile</Button></Link>
  </Card>
);

const Importer = ({ onParsed, hasOptimized }) => {
  const [busy, setBusy] = useState(false);
  const parse = async () => {
    setBusy(true);
    try { const { data } = await api.post("/resume-builder/parse", {}); onParsed(normalize(data?.resume || data)); toast.success("Resume imported. Review each section."); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="mx-auto max-w-xl text-center" data-testid="resume-importer">
      <Sparkles className="mx-auto mb-3 h-8 w-8 text-[#D4AF37]" />
      <h2 className="font-display text-xl text-zinc-100">Import your resume</h2>
      <p className="mt-2 text-sm text-zinc-400">{hasOptimized ? "Review the optimized version above, or import" : "AI reads"} your main resume (PDF or DOCX) and splits it into editable sections.</p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <Button onClick={parse} disabled={busy} data-testid="resume-parse-button">{busy ? <><Spinner />Reading your resume…</> : <><Wand2 className="h-4 w-4" />Import my main resume</>}</Button>
        <Button variant="ghost" onClick={() => onParsed(normalize({}))} disabled={busy} data-testid="resume-start-blank">Start from blank</Button>
      </div>
    </Card>
  );
};

const Toolbar = ({ template, dirty, saving, onSave, onTemplates, onDownload, onUse, using }) => (
  <div className="sticky top-16 z-10 -mx-4 mb-5 flex flex-wrap items-center gap-2 border-b border-[#27272A] bg-[#09090B]/85 px-4 py-3 backdrop-blur-xl sm:mx-0 sm:rounded-xl sm:border" data-testid="resume-toolbar">
    <Button size="sm" variant="outline" onClick={onTemplates} data-testid="open-template-picker"><LayoutTemplate className="h-4 w-4" />{templateById(template).name}</Button>
    <span className="mr-auto text-xs text-zinc-500" data-testid="resume-save-state">{saving ? "Saving…" : dirty ? "Unsaved changes" : "All changes saved"}</span>
    <Button size="sm" variant="ghost" onClick={onSave} disabled={!dirty || saving} data-testid="resume-save-button"><Save className="h-4 w-4" />Save</Button>
    <Button size="sm" variant="outline" onClick={onDownload} data-testid="resume-download-pdf"><Download className="h-4 w-4" />PDF</Button>
    <Button size="sm" onClick={onUse} disabled={using} data-testid="resume-use-as-main">{using ? <Spinner /> : <FileUp className="h-4 w-4" />}Save as my resume</Button>
  </div>
);

export default function ResumeStudio() {
  const [state, setState] = useState("loading");
  const [r, setR] = useState(null);
  const [template, setTemplate] = useState("modern");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(false);
  const [tab, setTab] = useState("edit");
  const latest = useRef({});
  const ver = useRef(0);
  latest.current = { r, template, state, dirty };
  const { upload, busy: using } = useResumeUpload();
  const [params, setParams] = useSearchParams();
  const jobId = params.get("job") || null;
  const view = params.get("view") === "library" ? "library" : "editor";
  const tailoredCount = useTailored().length;
  const [tailor, setTailor] = useState(() => (jobId ? readTailor(jobId) : null));
  const [job, setJob] = useState(() => tailor?.job || null);
  const [atsTool, setAtsTool] = useState(null);
  useEffect(() => {
    const t = jobId ? readTailor(jobId) : null;
    setTailor(t); setJob(t?.job || null);
    if (jobId && !t?.job?.title) loadJob(jobId).then((j) => j && setJob({ title: j.title, company: j.company }));
  }, [jobId]);
  useEffect(() => { loadTools().then((d) => setAtsTool(findAts(d?.tools) || null)); }, []);

  useEffect(() => {
    api.get("/resume-builder").then(({ data }) => {
      setTemplate(data?.template || "modern");
      if (data?.resume) { setR(normalize(data.resume)); setState("ready"); } else setState("import");
    }).catch((e) => setState([404, 405].includes(e.response?.status) ? "soon" : "error"));
  }, []);

  const save = useCallback(async (quiet) => {
    const { r: cur, template: tpl, state: st } = latest.current;
    if (st !== "ready" || !cur) return false;
    const v = ver.current;
    setSaving(true);
    try { await api.put("/resume-builder", { resume: cur, template: tpl }); if (ver.current === v) setDirty(false); if (!quiet) toast.success("Resume saved"); return true; }
    catch (e) { toast.error(errMsg(e)); return false; } finally { setSaving(false); }
  }, []);
  useEffect(() => { if (!dirty) return; const t = setTimeout(() => save(true), 2500); return () => clearTimeout(t); }, [dirty, r, template, save]);
  useEffect(() => {
    const k = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "s" && latest.current.state === "ready") { e.preventDefault(); if (latest.current.dirty) save(); } };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }, [save]);

  const touch = () => { ver.current += 1; setDirty(true); };
  const patch = (k) => (v) => { setR((x) => ({ ...x, [k]: v })); touch(); };
  const pickTemplate = (t) => { setTemplate(t); touch(); toast.success(`${templateById(t).name} template applied`); };
  const onParsed = (data) => { setR(data); setState("ready"); touch(); };
  const saveMain = async (tj) => {
    const f = pdfFile(r, template, tj);
    if (!(await upload(f))) return false;
    setCurrentResume(f.name);
    if (jobId) addTailored({ jobId, job, file_name: f.name, template });
    return true;
  };
  const useAsMain = async () => { if (dirty && !(await save(true))) return; await saveMain(jobId ? job : null); };
  const beforeCheck = async () => { if (state !== "ready" || !r) return true; if (dirty && !(await save(true))) return false; return saveMain(job); };
  const optimized = tailor?.optimized;
  const dropOptimized = () => { clearOptimized(jobId); setTailor((t) => ({ ...t, optimized: null })); };
  const useOptimized = (nr) => { setR(nr); setState("ready"); touch(); dropOptimized(); };
  const review = optimized && ["ready", "import"].includes(state) && <OptimizedReview optimized={optimized} current={r} onUse={useOptimized} onKeep={dropOptimized} />;

  if (state === "loading") return <Spinner className="h-6 w-6 text-[#D4AF37]" />;
  return (
    <div data-testid="resume-studio-page">
      <PageHeader title="Resume Studio" subtitle="Apply your Boost suggestions, pick a template and export an ATS-friendly PDF." />
      <HumanSupportBanner where="resume" />
      <div className="mb-5 inline-flex gap-1 rounded-full border border-[#27272A] p-1" data-testid="resume-studio-views">
        {[["editor", "Editor"], ["library", `Tailored versions${tailoredCount ? ` · ${tailoredCount}` : ""}`]].map(([k, l]) => (
          <button key={k} onClick={() => { const p = new URLSearchParams(params); k === "library" ? p.set("view", "library") : p.delete("view"); setParams(p); }}
            className={cn("rounded-full px-4 py-1.5 text-sm transition-colors", view === k ? "bg-[#D4AF37] text-black" : "text-zinc-400 hover:text-zinc-200")} data-testid={`resume-view-${k}`}>{l}</button>
        ))}
      </div>
      {view === "library" ? <TailoredLibrary /> : <>
      {jobId && state !== "soon" && state !== "error" && <TailorBanner jobId={jobId} job={job} atsTool={atsTool} beforeCheck={beforeCheck} editing={state === "ready"} />}
      {review}
      {state === "soon" ? <ComingSoon /> : state === "error" ? <p className="text-sm text-red-400" data-testid="resume-studio-error">Couldn't load Resume Studio. Please refresh.</p>
        : state === "import" ? <Importer onParsed={onParsed} hasOptimized={!!optimized} /> : (
        <>
          <Toolbar template={template} dirty={dirty} saving={saving} onSave={() => save()} onTemplates={() => setPicker(true)} onDownload={() => downloadPdf(r, template, jobId ? job : null)} onUse={useAsMain} using={using} />
          <div className="mb-4 flex gap-1 rounded-full border border-[#27272A] p-1 lg:hidden" data-testid="resume-mobile-tabs">
            {["edit", "preview"].map((t) => <button key={t} onClick={() => setTab(t)} className={cn("flex-1 rounded-full py-1.5 text-sm capitalize transition-colors", tab === t ? "bg-[#D4AF37] text-black" : "text-zinc-400")} data-testid={`resume-tab-${t}`}>{t}</button>)}
          </div>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className={cn("min-w-0 space-y-4", tab !== "edit" && "hidden lg:block")}>
              <Suggestions resume={r} jobId={jobId} content={tailor?.content} onUpdate={(fn) => { setR((x) => fn(x)); touch(); }} jobIdeas={tailor?.ideas || []} source={tailor?.toolTitle} onApply={(nr) => { setR(normalize(nr)); touch(); }} />
              <PersonalForm value={r.personal} onChange={patch("personal")} />
              <SummaryForm value={r.summary} onChange={patch("summary")} />
              <ListEditor kind="experience" items={r.experience} onChange={patch("experience")} jobId={jobId} />
              <ListEditor kind="education" items={r.education} onChange={patch("education")} />
              <SkillsEditor value={r.skills} onChange={patch("skills")} />
              <ListEditor kind="certifications" items={r.certifications} onChange={patch("certifications")} />
            </div>
            <div className={cn("min-w-0", tab !== "preview" && "hidden lg:block")}>
              <div className="lg:sticky lg:top-36 lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto lg:rounded-lg">
                <ScaledDoc data={r} template={template} className="overflow-hidden rounded-lg shadow-[0_20px_80px_-20px_rgba(212,175,55,0.25)] ring-1 ring-[#27272A]" testid="resume-live-preview" />
              </div>
            </div>
          </div>
          <TemplatePicker open={picker} onClose={() => setPicker(false)} value={template} onChange={pickTemplate} data={r} />
        </>
      )}
      </>}
    </div>
  );
}
